import * as angular from 'angular';
import { CurrentUserService } from 'services/currentUser';
import { ModalService } from 'common/source/services/modal';
import { LoggingService } from 'services/logging';
import { HippoApiService } from 'services/hippoApi';
import { hasSmartHomeStarterKit } from 'common/source/utilities/policyInfoHelper';
import { isMobile } from 'utilities/devices';
import { FlagshipService } from 'services/flagship';

interface SinglePolicyPageContainerScope extends angular.IScope {
    policyInfo?: CustomerPolicyInfo; // claims page only MAY have a policyInfo
    policyIndex?: RoutePolicyIndex; // claims page only MAY have a policyIndex
    smartHomeDiscount?: number;
    proMonitoringStates?: Array<string>;
}

// note: also used by Claims page which has an OPTIONAL single policy
export function stateForSinglePolicyPage(page: { module: angular.IModule }) {
    const stateModuleName = `${page.module.name}-module`;

    const module = angular.module(stateModuleName, [page.module.name]);

    const resolve = {
        policyInfo: function(currentUserService: CurrentUserService, $stateParams: PolicyStateParams) {
            'ngInject';

            return currentUserService.getCustomerPolicyInfoByIndex($stateParams.policyIndex);
        },
        smartHomeDiscount: function(
            currentUserService: CurrentUserService,
            loggingService: LoggingService,
            $stateParams: PolicyStateParams
        ) {
            'ngInject';

            // Use customerPolicyInfos cache
            return currentUserService
                .getCustomerPolicyInfoByIndex($stateParams.policyIndex)
                .then((result) => {
                    const _hasSmartHomeStarterKit = hasSmartHomeStarterKit(result);

                    if (!_hasSmartHomeStarterKit) {
                        return;
                    }

                    if (result.property_data.smart_home_pro_monitoring_kit) {
                        return currentUserService.getDiscount(result.id, 'property_data.smart_home_pro_monitoring_kit');
                    } else {
                        return currentUserService.getDiscount(result.id, 'property_data.smart_home_kit_activated');
                    }
                })
                .then((result) => {
                    if (result && result.data) {
                        if (result.data.property_data.smart_home_pro_monitoring_kit) {
                            return result.data.property_data.smart_home_pro_monitoring_kit.discount;
                        } else {
                            return result.data.property_data.smart_home_kit_activated.discount;
                        }
                    }
                })
                .catch((e) => {
                    loggingService.log('Error calculating smart home discount', e);
                });
        },
        proMonitoringStates: function(hippoApiService: HippoApiService) {
            'ngInject';

            return hippoApiService.getProMonitoringStates().then((states) => {
                return states;
            });
        }
    };

    function controller(
        $scope: SinglePolicyPageContainerScope,
        policyInfo: CustomerPolicyInfo,
        smartHomeDiscount: number,
        proMonitoringStates: Array<string>,
        $stateParams: PolicyStateParams,
        modalService: ModalService,
        $rootScope: angular.IRootScopeService,
        currentUserService: CurrentUserService,
        $window: angular.IWindowService,
        flagshipService: FlagshipService
    ) {
        'ngInject';

        $scope.policyIndex = $stateParams.policyIndex;
        $scope.policyInfo = policyInfo;
        $scope.smartHomeDiscount = smartHomeDiscount;
        $scope.proMonitoringStates = proMonitoringStates;

        // the policyInfo && here is because this function is also used by the Claims page
        // which may not have a policyInfo or policyIndex

        function loadMobileAppDownloadModal() {
            const isMobileDevice: boolean = isMobile();

            if (isMobileDevice && $window.localStorage.getItem('hasSeenMobileAppDownloadModal') === null) {
                $window.localStorage.setItem('hasSeenMobileAppDownloadModal', 'true');
                modalService.openUncloseableModal('mobile-app-download-login-modal');
            }
        }

        if (
            policyInfo &&
            policyInfo.status !== 'terminated' &&
            policyInfo.status !== 'expired' &&
            policyInfo.checkout_data.force_customer_agreement
        ) {
            modalService.openUncloseableModal('accept-terms-and-conditions');
        } else {
            if (flagshipService.getFeatureFlagStatus() === 'FeatureFlagStatus:Ready') {
                if (flagshipService.getFlag('mobileAppDownloadLoginModal') === true) {
                    loadMobileAppDownloadModal();
                }
            } else {
                $scope.$on('FeatureFlagStatus:Ready', () => {
                    if (flagshipService.getFlag('mobileAppDownloadLoginModal') === true) {
                        loadMobileAppDownloadModal();
                    }
                });
            }
        }

        function updateCustomerPolicyInfo(useCache: boolean = true) {
            currentUserService
                .getCustomerPolicyInfoByIndex($stateParams.policyIndex, useCache)
                .then((policyInfoByIndex) => {
                    $scope.policyInfo = policyInfoByIndex;
                });
        }

        const deregisterRootScopeListener = $rootScope.$on('updatedCustomerPolicyInfos', () =>
            updateCustomerPolicyInfo(false)
        );

        $scope.$on('$destroy', () => {
            deregisterRootScopeListener();
        });
    }

    const template = `<${
        page.module.name
    } policy-info="policyInfo" smart-home-discount="smartHomeDiscount"  pro-monitoring-states="proMonitoringStates"
                      policy-index="policyIndex"></${page.module.name}>`;

    return {
        controller: controller,
        template: template,
        resolve: resolve,
        module: module
    };
}
